import { isBrowserDefined } from "../services/browserService";
import parse from "node-html-parser";
import { marked } from "marked";
import { ChatThread } from "../../interfaces/api/chat";
import { Status } from "./enums";
import { ClaimStatus, InvoiceStatus } from "../../interfaces/screens/billing";

/**
 * Opens a link in a new browser tab if the browser environment is defined.
 *
 * @param {string} link - The URL to be opened.
 */
export const openLink = (link: string) => {
  if (!isBrowserDefined()) return;
  window.open(link, "_blank");
};

/**
 * Extracts text content from HTML or Markdown and returns an excerpt limited by word or character count.
 *
 * @param {string} content - The HTML or Markdown string to extract content from.
 * @param {number} [wordLimit=50] - The maximum number of words to include in the excerpt.
 * @param {string} [tail="... (Read more)"] - The string to append at the end of the excerpt if it is truncated.
 * @param {number} [charLimit] - The maximum number of characters to include in the excerpt.
 *
 * @returns {string} The extracted text content with the specified limits and tail if truncated.
 */
export const extractContent = (
  content: string,
  wordLimit: number = 50,
  tail: string = "... (Read more)",
  charLimit?: number,
): string => {
  if (typeof content !== "string") {
    content = String(content);
  }

  let processedContent = "";

  // Process content similar to renderArticleContent
  if (isHtml(content)) {
    processedContent = content;
  } else if (isMarkdown(content)) {
    processedContent = marked.parse(content || "", {
      async: false,
      breaks: true,
      gfm: true,
    });
  } else {
    processedContent = content;
  }

  const root = parse(processedContent);
  const textContent = root.textContent || "";

  let excerpt = "";
  let currentLength = 0;

  if (charLimit !== undefined && charLimit < textContent.length) {
    excerpt = textContent.slice(0, charLimit);
  } else {
    const words = textContent.split(/\s+/);
    for (let i = 0; i < words.length && i < wordLimit; i++) {
      if (
        charLimit !== undefined &&
        currentLength + words[i].length + 1 > charLimit
      ) {
        excerpt = textContent.slice(0, currentLength).trim();
        break;
      }
      excerpt += words[i] + " ";
      currentLength += words[i].length + 1;
    }
  }
  return excerpt.length < textContent.length
    ? `${excerpt.trim()}${tail}`
    : excerpt.trim();
};

/**
 * Truncates a given text to a specified character limit and appends ellipsis if truncated.
 *
 * @param {string} text - The text to be truncated.
 * @param {number} [limit=50] - The maximum number of characters to include in the truncated text.
 *
 * @returns {string} The truncated text with ellipsis if truncated.
 */
export const truncateText = (text: string, limit: number = 50) => {
  if (!text) return "";
  if (text.length <= limit) return text;
  return `${text.substring(0, limit)}...`;
};

/**
 * Determines if the content is in Markdown format.
 *
 * @param {string | any} content - The content string to check.
 * @returns {boolean} True if the content appears to be Markdown; otherwise, false.
 */
export const isMarkdown = (content: any): boolean => {
  if (typeof content !== "string") return false;

  const markdownPatterns = [
    /^#{1,6}\s/, // Markdown headings (#, ##, ###, etc.)
    /^\s*[-+*]\s+/, // Unordered lists (-, +, or *)
    /^\s*\d+\.\s+/, // Ordered lists (1., 2., etc.)
    /\*\*(.*?)\*\*/, // Bold (**bold**)
    /\*(.*?)\*/, // Italic (*italic*)
    /`([^`]+)`/, // Inline code (`code`)
    /^>\s+/, // Blockquotes (>)
    /!\[.*?\]\(.*?\)/, // Images (![alt text](url))
    /\[.*?\]\(.*?\)/, // Links ([text](url))
  ];

  return markdownPatterns.some((pattern) => pattern.test(content));
};

/**
 * Processes content that might contain HTML, Markdown, or plain text.
 *
 * @param {string | any} content - Mixed content string.
 * @returns {string} Processed HTML.
 */
export const processContent = (content: any): string => {
  if (typeof content !== "string") {
    content = String(content); // Ensure content is a string
  }

  let processedContent = "";

  // Detect if content is Markdown or HTML
  if (isHtml(content)) {
    processedContent = content;
  } else if (isMarkdown(content)) {
    // Pre-process the content to handle special cases
    let preProcessedContent = content
      // Fix newlines
      .replace(/\n/g, "  \n") // Add two spaces before newline for proper line breaks
      // Fix headers that might be missing space after #
      .replace(/^(#{1,6})([^#\s])/gm, "$1 $2")
      // Fix table formatting
      .replace(/\|([^|]+)\|/g, (match: string) => {
        // Ensure proper spacing in table cells
        return match.replace(/\s*\|\s*/g, " | ");
      });

    // Process the content with marked
    processedContent = marked.parse(preProcessedContent || "", {
      async: false,
      breaks: true,
      gfm: true,
    });

    // Add custom styling for headers
    processedContent = processedContent
      .replace(/<h1>/g, '<h1 class="text-2xl font-bold mb-4">')
      .replace(/<h2>/g, '<h2 class="text-xl font-bold mb-3">')
      .replace(/<h3>/g, '<h3 class="text-lg font-bold mb-2">')
      .replace(/<h4>/g, '<h4 class="text-base font-bold mb-2">')
      .replace(/<h5>/g, '<h5 class="text-sm font-bold mb-1">')
      .replace(/<h6>/g, '<h6 class="text-xs font-bold mb-1">')
      // Add styling for lists
      .replace(/<ul>/g, '<ul class="list-disc pl-5 mb-4">')
      .replace(/<ol>/g, '<ol class="list-decimal pl-5 mb-4">')
      // Add styling for blockquotes
      .replace(
        /<blockquote>/g,
        '<blockquote class="border-l-4 border-gray-300 pl-4 italic my-4">',
      )
      // Add styling for code blocks
      .replace(
        /<pre>/g,
        '<pre class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto my-4">',
      )
      .replace(
        /<code>/g,
        '<code class="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm">',
      )
      // Add styling for tables
      .replace(
        /<table>/g,
        '<table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 my-4">',
      )
      .replace(/<thead>/g, '<thead class="bg-gray-50 dark:bg-gray-800">')
      .replace(
        /<th>/g,
        '<th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">',
      )
      .replace(
        /<td>/g,
        '<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">',
      )
      // Ensure proper line breaks
      .replace(/\n/g, "<br>");
  } else {
    // For plain text, convert newlines to <br> tags
    processedContent = content.replace(/\n/g, "<br>");
  }

  return processedContent;
};

/**
 * Determines whether a string contains valid HTML.
 *
 * @param {string} content - The input string to check.
 * @returns {boolean} True if the content contains valid HTML; otherwise, false.
 */
export const isHtml = (content: string): boolean => {
  if (!content || typeof content !== "string") {
    return false;
  }

  // Check if the content contains opening and closing HTML tags
  const htmlPattern = /<([a-z][\w-]*)\b[^>]*>(.*?)<\/\1>/i; // Matches full opening/closing tags
  const selfClosingPattern = /<([a-z][\w-]*)\b[^>]*\/>/i; // Matches self-closing tags (e.g., <img />)

  return htmlPattern.test(content) || selfClosingPattern.test(content);
};

/**
 * Determines if the content is plain text (neither Markdown nor HTML).
 *
 * @param {string} content - The content string to check.
 * @returns {boolean} True if the content is plain text; otherwise, false.
 */
export const isPlainText = (content: string): boolean => {
  return !isMarkdown(content) && !isHtml(content);
};

/**
 * Groups chat threads by date (Today, Yesterday, Earlier).
 * @param {ChatThread[]} threads - The list of chat threads.
 * @returns {Record<string, ChatThread[]>} Grouped threads by date.
 */
export function groupThreadsByDate(
  threads: ChatThread[],
): Record<string, ChatThread[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  return threads.reduce(
    (acc: Record<string, ChatThread[]>, thread: ChatThread) => {
      const threadDate = new Date(thread.created_at);
      let group = "Earlier";
      if (threadDate >= today) {
        group = "Today";
      } else if (threadDate >= yesterday) {
        group = "Yesterday";
      }
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(thread);
      return acc;
    },
    {},
  );
}

export const mapInvoiceStatusToChipStatus = (
  invoiceStatus: InvoiceStatus,
): Status => {
  switch (invoiceStatus) {
    case "paid":
    case "void":
      return Status.DONE;
    case "partially_paid":
    case "pending_insurance":
    case "issued":
      return Status.IN_PROGRESS;
    case "draft":
    default:
      return Status.TODO;
  }
};

export const mapInvoiceStatusToText = (
  invoiceStatus: InvoiceStatus,
): string => {
  switch (invoiceStatus) {
    case "draft":
      return "Draft";
    case "issued":
      return "Issued";
    case "partially_paid":
      return "Partially Paid";
    case "paid":
      return "Paid";
    case "pending_insurance":
      return "Pending Insurance";
    case "void":
      return "Void";
    default:
      return invoiceStatus;
  }
};

export const mapClaimStatusToText = (claimStatus: ClaimStatus): string => {
  switch (claimStatus) {
    case "submitted":
      return "Submitted";
    case "under_review":
      return "Under Review";
    case "partially_approved":
      return "Partially Approved";
    case "approved":
      return "Approved";
    case "rejected":
      return "Rejected";
    case "paid":
      return "Paid";
    case "denied":
      return "Denied";
    case "pending_documentation":
      return "Pending Documentation";
    case "draft":
      return "Draft";
    default:
      return claimStatus;
  }
};

export const mapClaimStatusToChipStatus = (
  claimStatus: ClaimStatus,
): Status => {
  switch (claimStatus) {
    case "paid":
    case "approved":
      return Status.DONE;
    case "partially_approved":
    case "submitted":
    case "under_review":
    case "pending_documentation":
      return Status.IN_PROGRESS;
    case "rejected":
    case "denied":
      return Status.CANCELLED;
    case "draft":
    default:
      return Status.TODO;
  }
};
